import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import Header from "@/components/layout/Header";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { FileText, Users, Bell } from "lucide-react";
import ManagerRequestsTab from "@/components/manager/ManagerRequestsTab";
import NotificationBell from "@/components/notifications/NotificationBell";

interface Company {
  id: string;
  name: string;
  sector: string;
  employee_count: number;
}

const ManagerDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [company, setCompany] = useState<Company | null>(null);
  const [managerProfileId, setManagerProfileId] = useState<string | null>(null);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    checkUserRole();
  }, []);

  const checkUserRole = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        navigate('/auth');
        return;
      }

      // Check if user has manager role
      const { data: roleData, error: roleError } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id)
        .eq('role', 'manager')
        .maybeSingle();

      if (roleError || !roleData) {
        toast({
          variant: "destructive",
          title: "Accès refusé",
          description: "Vous n'avez pas les droits de manager"
        });
        navigate('/auth');
        return;
      }

      // Get profile id
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('id, company_id')
        .eq('user_id', user.id)
        .single();

      if (profileError || !profileData) {
        navigate('/auth');
        return;
      }

      setManagerProfileId(profileData.id);

      // Get company info
      if (profileData.company_id) {
        const { data: companyData } = await supabase
          .from('companies')
          .select('id, name, sector, employee_count')
          .eq('id', profileData.company_id)
          .single();

        if (companyData) {
          setCompany(companyData);
        }
      }

    } catch (error) {
      console.error('Error checking role:', error);
      navigate('/auth');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header showLogoutButton={true} />
        <div className="container mx-auto px-4 pt-24">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header showLogoutButton={true} />
      
      <main className="container mx-auto px-4 pt-24 pb-12">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-foreground">
              Tableau de bord Manager
            </h1>
            <p className="text-muted-foreground mt-1">
              Gérez les demandes de télétravail de votre équipe
            </p>
          </div>
          
          <div className="flex items-center gap-4">
            <NotificationBell />
          </div>
        </div>

        <Tabs defaultValue="requests" className="space-y-6">
          <TabsList className="grid w-full md:w-auto md:inline-grid grid-cols-2 md:grid-cols-2 gap-1">
            <TabsTrigger value="requests" className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              <span className="hidden sm:inline">Demandes</span>
            </TabsTrigger>
            <TabsTrigger value="team" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              <span className="hidden sm:inline">Mon équipe</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="requests">
            <ManagerRequestsTab managerProfileId={managerProfileId} />
          </TabsContent>

          <TabsContent value="team">
            <div className="text-center py-12 text-muted-foreground">
              Fonctionnalité à venir : liste des membres de votre équipe
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default ManagerDashboard;